import { useGetMillwrightsByOutageIdQuery } from '@/apis/millwrights/millwrightsApi';
import { MillwrightsList } from '@/pages/Millwrights/components';
import { ChangeEvent, FC } from 'react';
import { usePagination, useSearch } from '@/hooks';
import {
  INITIAL_PAGE,
  LIST_PAGE_SIZE,
  millwrightsSortOptions,
} from '@/constants';
import { Select, SelectOption } from '@data-products-and-ai/react-components';
import { useMillwrightsSort } from '@/pages/Millwrights/hooks';
import { toPascalCase } from '@/utils';

interface OutageMilwrightsProps {
  outageId: number;
}

export const OutageMillwrights: FC<OutageMilwrightsProps> = ({ outageId }) => {
  const { debouncedQuery, query, handleSearch } = useSearch();
  const { currentPage, pageSize, setPage } = usePagination({
    initialPage: INITIAL_PAGE,
    initialPageSize: LIST_PAGE_SIZE,
  });
  const { sortOption, updateSortOption } = useMillwrightsSort(
    millwrightsSortOptions[0],
  );

  const sortBy = (toPascalCase(sortOption?.sortBy) ?? 'Name') as
    | 'Name'
    | 'Risk';

  const {
    data: millwrightsData,
    isLoading,
    isFetching,
  } = useGetMillwrightsByOutageIdQuery(
    {
      outageId,
      q: debouncedQuery,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
      sortDir: sortOption.sortDir,
      sortBy,
    },
    {
      skip: !outageId,
    },
  );
  const millwrights = millwrightsData?.results || [];
  const totalItems = millwrightsData?.total ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleSortChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const sortOption = millwrightsSortOptions.find(
      (option) => option.value === event.target.value,
    );

    if (sortOption) {
      updateSortOption(sortOption);
      setPage(INITIAL_PAGE);
    }
  };

  return (
    <MillwrightsList
      hasSearch
      hasPagination
      list={millwrights}
      totalItems={totalItems}
      totalPages={totalPages}
      currentPage={currentPage}
      header="Active millwrights"
      loading={isLoading || isFetching}
      searchQuery={query}
      sortComponent={
        <Select defaultValue={sortOption.value} onChange={handleSortChange}>
          {millwrightsSortOptions.map((option) => (
            <SelectOption
              key={option.value}
              value={option.value}
              label={option.label}
            />
          ))}
        </Select>
      }
      onSearchDone={handleSearch}
      onPageChange={handlePageChange}
    />
  );
};
